import { Component } from '@angular/core';
import {
    LsqApiServiceListSource,
    SortDirectionEnterprise
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    UpdateBankAccountVerificationRequest,
    UpdateBankAccountVerificationService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    ListSupplierPaymentMethodsRequest,
    ListSupplierPaymentMethodsResponse,
    ListSupplierPaymentMethodsService,
    PaymentMethodStatus,
    SupplierPaymentMethodQueryRow,
    AutoVerifyStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ClassNames,
    forLifeOf,
    LifecycleAware,
    Modal,
    FeatureFlagService
} from '@lsq-frontend/common-ui';
import { ClaimService, Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { TAKE_ALL } from '../../../../supply-chain/constants';
import {
    PaymentMethodStatusDisplay,
    PaymentMethodTypeDisplay,
    AutoVerifyStatusDisplay
} from '../../../models';
import { PaymentMethodStatusUpdateModalComponent } from '../../modals/payment-method-status-update-modal.component';
import { ManageSupplierService } from '../manage-supplier.service';
import {
    PaymentMethod,
    PutPaymentMethodService,
    GetPaymentMethodByCompanyKeyService
} from '@lsq-frontend/remote-services/pmt-restapi';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { dump } from 'js-yaml';
@LifecycleAware()
@Component({
    selector: 'manage-supplier-payment-methods',
    styles: [
        `
            :host ::ng-deep div.popover.popover-content.sm {
                width: auto;
            }
        `
    ],
    template: `
        <div class="manage-supplier-payment-method row">
            <div class="col-12 col-lg-8" [style.marginBottom]="'2rem'">
                <card [headerTitle]="'Payment Methods'">
                    <div card-body>
                        <lsq-data-table [dataSource]="dataSource">
                            <lsq-column
                                [column]="{
                                    key: 'default',
                                    header: 'Default',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-icon *ngIf="row.default" [type]="icons.Checkmark">
                                    </lsq-icon>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    key: 'paymentMethodType',
                                    header: 'Payment Type',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    {{ paymentMethodTypeDisplay[row.paymentMethodType] }}
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    key: 'accountName',
                                    header: 'Account',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-truncate
                                        [value]="row.accountName"
                                        [maxLength]="25"
                                    ></lsq-truncate>
                                    (...{{ row.accountNumber }})
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    key: 'autoVerifyStatus',
                                    header: 'Auto-Verify Status',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <popover-content #response>
                                        <h3>{{ row.validationResponseTitle }}</h3>
                                        <pre>{{ row.validationResponse }}</pre>
                                    </popover-content>
                                    <span
                                        [popover]="response"
                                        [popoverPlacement]="right"
                                        [popoverOnHover]="true"
                                    >
                                        {{ autoVerifyStatusDisplay[row.autoVerifyStatus] }}
                                    </span>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    key: 'action',
                                    header: 'Verify',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-switch
                                        [name]="'verified'"
                                        [ngModel]="
                                            row.paymentMethodStatus === paymentMethodStatus.VERIFIED
                                        "
                                        [disabled]="!(canEditSuppliers$ | async)"
                                        (click)="openPaymentMethodVerificationModal($event, row)"
                                    >
                                    </lsq-switch>
                                </ng-template>
                            </lsq-column>

                            <ng-template #noDataContent>
                                <no-data
                                    [icon]="icons.FaMoneyCheck"
                                    titleText="No payment methods to show yet."
                                    description="Payment methods will show here once they’ve been added to the account."
                                >
                                </no-data>
                            </ng-template>
                        </lsq-data-table>
                    </div>
                </card>
            </div>
            <div class="col-12 col-lg-4">
                <data-card [lightBorder]="true">
                    <p class="header" [style.marginBottom]="'1rem'">Additional Information</p>
                    <p class="body text-dark-gray" [style.marginBottom]="'1rem'">
                        This shows the supplier’s current payment methods listed in their system.
                        Once a payment method has been properly reviewed, it can be set to either
                        “Verified” or “In Review” when using the appropriate toggle in the Actions
                        column.
                    </p>
                </data-card>
            </div>
        </div>
    `
})
export class ManageSupplierPaymentMethodsComponent {
    public currentRelationship$ = this._manageSupplierService.currentSupplierRelationship$;
    public dataSource;
    public canEditSuppliers$ = this._claimService.isOn(DashboardClaim.AdminEditSupplier);
    public icons = Icons;
    public paymentMethodTypeDisplay = PaymentMethodTypeDisplay;
    public paymentMethodStatus = PaymentMethodStatus;
    public paymentMethodStatusDisplay = PaymentMethodStatusDisplay;
    public autoVerifyStatusDisplay = AutoVerifyStatusDisplay;
    public paymentMethods: PaymentMethod[];
    public userService: UserService;
    constructor(
        private _claimService: ClaimService,
        private _modalService: ModalService,
        private _manageSupplierService: ManageSupplierService,
        private _listSupplierPaymentMethodsService: ListSupplierPaymentMethodsService,
        private _updateBankAccountVerificationService: UpdateBankAccountVerificationService,
        private _putPaymentMethodService: PutPaymentMethodService,
        private _featureFlagService: FeatureFlagService,
        private _getPaymentMethodByCompanyKeyService: GetPaymentMethodByCompanyKeyService
    ) {
        this.dataSource = new LsqApiServiceListSource<
            ListSupplierPaymentMethodsResponse,
            ListSupplierPaymentMethodsRequest,
            SupplierPaymentMethodQueryRow
        >(this._listSupplierPaymentMethodsService, undefined, (scfRow) => {
            const jsonResponse =
                this.paymentMethods.find((pmRow) => pmRow.key === scfRow.paymentMethodKey)
                    ?.validationApiResponse === undefined
                    ? {}
                    : this.paymentMethods.find((pmRow) => pmRow.key === scfRow.paymentMethodKey)
                          ?.validationApiResponse;
            return {
                ...scfRow,
                autoVerifyStatus:
                    this.paymentMethods.find((pmRow) => pmRow.key === scfRow.paymentMethodKey)
                        ?.accountVerification === undefined
                        ? AutoVerifyStatus.UNCHECKED
                        : AutoVerifyStatus[
                              this.paymentMethods.find(
                                  (pmRow) => pmRow.key === scfRow.paymentMethodKey
                              )?.accountVerification
                          ],
                validationResponse: jsonResponse
                    ? dump(jsonResponse['errors']?.map((x) => x.message).join('\n') || jsonResponse)
                    : dump({}),
                validationResponseTitle:
                    Object.keys(jsonResponse).length === 0
                        ? 'No Response'
                        : jsonResponse['errors']
                        ? 'Error'
                        : jsonResponse['accountStatus']['additionalDetails']['message']
            };
        });
        combineLatest([this.currentRelationship$, this._manageSupplierService.supplierDetails$])
            .pipe(
                forLifeOf(this),
                filter(([relationship, supplierDetails]) =>
                    Boolean(relationship && supplierDetails)
                )
            )
            .subscribe(([relationship, supplierDetails]) => {
                const companyKey = supplierDetails.supplierCompanyKey;

                this._getPaymentMethodByCompanyKeyService.execute(companyKey).then((bankAccts) => {
                    this.paymentMethods = bankAccts;

                    this.dataSource.dispatchRequest({
                        supplierKey: relationship.supplierKey,
                        take: TAKE_ALL,
                        skip: 0,
                        sortColumn: 'default',
                        sortDirection: SortDirectionEnterprise.Desc
                    });
                });
            });
    }

    public async openPaymentMethodVerificationModal(
        event: Event,
        row: SupplierPaymentMethodQueryRow
    ): Promise<void> {
        event.preventDefault();

        if (!this._claimService.isOnSync(DashboardClaim.AdminEditSupplier)) {
            return;
        }

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const currentVerificationStatus = row.paymentMethodStatus === PaymentMethodStatus.VERIFIED;

        this._modalService.showModal(
            new Modal({
                id: 'set-supplier-eligibility-confirmation-modal',
                title: currentVerificationStatus
                    ? 'Change Payment Method to In Review'
                    : 'Verify Payment Method',
                bodyComponent: PaymentMethodStatusUpdateModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                showCloseButton: true,
                okButtonSize: ClassNames.Block,
                okButtonText: currentVerificationStatus
                    ? 'Change to In Review'
                    : 'Verify Payment Method',
                isSubmitting$: isSubmittingSubject,
                data: {
                    paymentMethod: row
                },
                onOkClick: async () => {
                    const relationship =
                        await this._manageSupplierService.currentSupplierRelationship$
                            .pipe(first())
                            .toPromise();
                    isSubmittingSubject.next(true);
                    await this._updateBankAccountVerificationService.executeOnce(
                        new UpdateBankAccountVerificationRequest({
                            bankAccountKey: row.paymentMethodKey,
                            relationshipKey: relationship.relationshipKey,
                            isVerified: !currentVerificationStatus
                        })
                    );

                    // update verification in payments domain
                    if (this._featureFlagService.isOnSync(FeatureFlags.LegalEntityConnection)) {
                        let isVerified = !currentVerificationStatus;
                        let isDefault = false;
                        await this._putPaymentMethodService.execute(
                            row.paymentMethodKey,
                            isVerified,
                            isDefault
                        );
                    }

                    isSubmittingSubject.next(false);
                    this._modalService.closeModal();
                    this.dataSource.dispatchRequest();
                },
                onCancelClick: () => this._modalService.closeModal()
            })
        );
    }
}
